/**
 * AdminRoutes - Zentrale Route-Definition für den Admin-Bereich
 *
 * Verwendet nested routes mit dem AdminLayout als persistentem Wrapper.
 * Das Layout (Header, Sidebar) bleibt beim Seitenwechsel erhalten.
 *
 * Rollen:
 * - admin: Zugriff auf alle Standard-Admin-Funktionen
 * - root_admin: Zusätzlicher Zugriff auf sensible Bereiche (AI, Debug, etc.)
 * - demo_admin: Lesezugriff auf Admin-Bereich (keine Schreibrechte)
 */

import React from 'react';
import { Route, Navigate, Outlet } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Loader2 } from 'lucide-react';
import { HStack } from '@/components/core';

// Lazy-loaded Admin Pages
const AdminDashboard = React.lazy(() => import('@/pages/AdminDashboard'));
const AdminCharacters = React.lazy(() => import('@/pages/AdminCharacters'));
const AdminCharacterSettings = React.lazy(() => import('@/pages/AdminCharacterSettings'));
const AdminCharacterForm = React.lazy(() => import('@/pages/AdminCharacterForm'));
const AdminUsers = React.lazy(() => import('@/pages/AdminUsers'));
const AdminPartners = React.lazy(() => import('@/pages/AdminPartners'));
const AdminPartnerPricing = React.lazy(() => import('@/pages/AdminPartnerPricing'));
const AdminPricingPresets = React.lazy(() => import('@/pages/AdminPricingPresets'));
const AdminSettings = React.lazy(() => import('@/pages/AdminSettings'));
const AdminAISettings = React.lazy(() => import('@/pages/AdminAISettings'));
const AdminAuditLogs = React.lazy(() => import('@/pages/admin/AdminAuditLogs'));
const AdminDesign = React.lazy(() => import('@/pages/admin/AdminDesign'));
const AdminPlatformTariff = React.lazy(() => import('@/pages/AdminPlatformTariff'));
const AdminSwipeSettings = React.lazy(() => import('@/pages/admin/AdminSwipeSettings'));
const AdminSwipeAnalytics = React.lazy(() => import('@/pages/admin/AdminSwipeAnalytics'));
const AdminPreferenceInsights = React.lazy(() => import('@/pages/admin/AdminPreferenceInsights'));
const AdminRevenueAnalytics = React.lazy(() => import('@/pages/admin/AdminRevenueAnalytics'));
const AdminCharacterLocations = React.lazy(() => import('@/pages/admin/AdminCharacterLocations'));
const AdminDebug = React.lazy(() => import('@/pages/admin/AdminDebug'));
const AdminGifts = React.lazy(() => import('@/pages/admin/AdminGifts'));
const AdminAffiliates = React.lazy(() => import('@/pages/admin/AdminAffiliates'));
const AdminDailyChallenges = React.lazy(() => import('@/pages/admin/AdminDailyChallenges'));
const AdminAchievements = React.lazy(() => import('@/pages/admin/AdminAchievements'));
const AdminLanguageSettings = React.lazy(() => import('@/pages/admin/AdminLanguageSettings'));
const AdminTextOverrides = React.lazy(() => import('@/pages/admin/AdminTextOverrides'));
const AdminCharacterAttributes = React.lazy(() => import('@/pages/AdminCharacterAttributes'));
const AdminAnalytics = React.lazy(() => import('@/pages/AdminAnalytics'));
const AdminNotificationSettings = React.lazy(() => import('@/pages/admin/AdminNotificationSettings'));
const AdminEmailSettings = React.lazy(() => import('@/pages/admin/AdminEmailSettings'));
const AdminEmailManagement = React.lazy(() => import('@/pages/AdminEmailManagement'));
const AdminAICosts = React.lazy(() => import('@/pages/admin/AdminAICosts'));
const AdminAIFallbackMonitoring = React.lazy(() => import('@/pages/admin/AdminAIFallbackMonitoring'));
const AdminMemoriesPage = React.lazy(() => import('@/pages/admin/AdminMemories'));
const AdminSupport = React.lazy(() => import('@/pages/admin/AdminSupport'));
const AdminSystemMonitoring = React.lazy(() => import('@/pages/admin/AdminSystemMonitoring'));
const AdminDataTransfer = React.lazy(() => import('@/pages/admin/AdminDataTransfer'));
const AdminCreatorSpace = React.lazy(() => import('@/pages/admin/AdminCreatorSpace'));
const AdminLegalSettings = React.lazy(() => import('@/pages/admin/AdminLegalSettings'));
const AdminSafetyReports = React.lazy(() => import('@/pages/admin/AdminSafetyReports'));
const AdminUserChats = React.lazy(() => import('@/pages/admin/AdminUserChats'));
const AdminBilling = React.lazy(() => import('@/pages/admin/AdminBilling'));
const AdminPartnerBilling = React.lazy(() => import('@/pages/admin/AdminPartnerBilling'));
const AdminUserSubscriptions = React.lazy(() => import('@/pages/admin/AdminUserSubscriptions'));
const AdminPaymentProviders = React.lazy(() => import('@/pages/admin/AdminPaymentProviders'));
/**
 * RootAdminGuard - Schützt Routen, die nur für root_admin zugänglich sind
 */
function RootAdminGuard() {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <HStack align="center" justify="center" className="py-12">
        <Loader2 className="h-8 w-8 animate-spin text-brand-primary" />
      </HStack>
    );
  }

  if (user?.role !== 'root_admin') {
    return <Navigate to="/admin/dashboard" replace />;
  }

  return <Outlet />;
}

/**
 * Suspense Wrapper für konsistentes Loading
 */
function SuspenseWrapper({ children }: { children: React.ReactNode }) {
  return (
    <React.Suspense
      fallback={
        <HStack align="center" justify="center" className="py-12">
          <Loader2 className="h-8 w-8 animate-spin text-brand-primary" />
        </HStack>
      }
    >
      {children}
    </React.Suspense>
  );
}

/**
 * Admin Route Konfiguration
 *
 * Verwendung in App.tsx:
 * ```tsx
 * <Route path="/admin" element={<AdminLayout />}>
 *   {adminRoutes}
 * </Route>
 * ```
 */
export const adminRoutes = (
  <>
    {/* Dashboard */}
    <Route index element={<SuspenseWrapper><AdminDashboard /></SuspenseWrapper>} />
    <Route path="dashboard" element={<SuspenseWrapper><AdminDashboard /></SuspenseWrapper>} />

    {/* Characters */}
    <Route path="characters" element={<SuspenseWrapper><AdminCharacters /></SuspenseWrapper>} />
    <Route path="characters/new" element={<SuspenseWrapper><AdminCharacterForm /></SuspenseWrapper>} />
    <Route path="characters/:id" element={<SuspenseWrapper><AdminCharacterForm /></SuspenseWrapper>} />
    <Route path="characters/:id/edit" element={<SuspenseWrapper><AdminCharacterForm /></SuspenseWrapper>} />
    <Route path="character-settings" element={<SuspenseWrapper><AdminCharacterSettings /></SuspenseWrapper>} />
    <Route path="character-locations" element={<SuspenseWrapper><AdminCharacterLocations /></SuspenseWrapper>} />
    <Route path="character-attributes" element={<SuspenseWrapper><AdminCharacterAttributes /></SuspenseWrapper>} />

    {/* Users & Content */}
    <Route path="users" element={<SuspenseWrapper><AdminUsers /></SuspenseWrapper>} />
    <Route path="support" element={<SuspenseWrapper><AdminSupport /></SuspenseWrapper>} />
    <Route path="moderation" element={<SuspenseWrapper><AdminSafetyReports /></SuspenseWrapper>} />
    <Route path="safety/reports" element={<SuspenseWrapper><AdminSafetyReports /></SuspenseWrapper>} />
    <Route path="user-chats" element={<SuspenseWrapper><AdminUserChats /></SuspenseWrapper>} />
    <Route path="memories" element={<SuspenseWrapper><AdminMemoriesPage /></SuspenseWrapper>} />
    <Route path="creator-space" element={<SuspenseWrapper><AdminCreatorSpace /></SuspenseWrapper>} />

    {/* Swipe & Matching */}
    <Route path="swipe/settings" element={<SuspenseWrapper><AdminSwipeSettings /></SuspenseWrapper>} />
    <Route path="swipe/analytics" element={<SuspenseWrapper><AdminSwipeAnalytics /></SuspenseWrapper>} />
    <Route path="preference-insights" element={<SuspenseWrapper><AdminPreferenceInsights /></SuspenseWrapper>} />

    {/* Gamification */}
    <Route path="daily-challenges" element={<SuspenseWrapper><AdminDailyChallenges /></SuspenseWrapper>} />
    <Route path="achievements" element={<SuspenseWrapper><AdminAchievements /></SuspenseWrapper>} />
    <Route path="gifts" element={<SuspenseWrapper><AdminGifts /></SuspenseWrapper>} />
    <Route path="affiliates" element={<SuspenseWrapper><AdminAffiliates /></SuspenseWrapper>} />

    {/* Billing & Monetization */}
    <Route path="billing" element={<SuspenseWrapper><AdminBilling /></SuspenseWrapper>} />
    <Route path="partner-billing" element={<SuspenseWrapper><AdminPartnerBilling /></SuspenseWrapper>} />
    <Route path="payment-providers" element={<SuspenseWrapper><AdminPaymentProviders /></SuspenseWrapper>} />
    <Route path="user-subscriptions" element={<SuspenseWrapper><AdminUserSubscriptions /></SuspenseWrapper>} />

    {/* Analytics */}
    <Route path="analytics" element={<SuspenseWrapper><AdminAnalytics /></SuspenseWrapper>} />
    <Route path="analytics/revenue" element={<SuspenseWrapper><AdminRevenueAnalytics /></SuspenseWrapper>} />

    {/* Communication */}
    <Route path="notification-settings" element={<SuspenseWrapper><AdminNotificationSettings /></SuspenseWrapper>} />
    <Route path="settings/notifications" element={<SuspenseWrapper><AdminNotificationSettings /></SuspenseWrapper>} />
    <Route path="email-settings" element={<SuspenseWrapper><AdminEmailSettings /></SuspenseWrapper>} />
    <Route path="email-management" element={<SuspenseWrapper><AdminEmailManagement /></SuspenseWrapper>} />
    <Route path="legal" element={<SuspenseWrapper><AdminLegalSettings /></SuspenseWrapper>} />

    {/* System Settings */}
    <Route path="settings" element={<SuspenseWrapper><AdminSettings /></SuspenseWrapper>} />
    <Route path="design" element={<SuspenseWrapper><AdminDesign /></SuspenseWrapper>} />
    <Route path="language-settings" element={<SuspenseWrapper><AdminLanguageSettings /></SuspenseWrapper>} />
    <Route path="text-overrides" element={<SuspenseWrapper><AdminTextOverrides /></SuspenseWrapper>} />

    {/* Redirects für alte URLs */}
    <Route path="pricing" element={<Navigate to="/admin/billing" replace />} />
    <Route path="billing-plans" element={<Navigate to="/admin/billing" replace />} />
    <Route path="coin-packages" element={<Navigate to="/admin/billing" replace />} />
    <Route path="attributes" element={<Navigate to="/admin/character-settings" replace />} />
    <Route path="settings/character-behavior" element={<Navigate to="/admin/character-settings" replace />} />
    <Route path="settings/peak-and-badge" element={<Navigate to="/admin/character-settings" replace />} />
    <Route path="profile-visits" element={<Navigate to="/admin/character-settings" replace />} />
    <Route path="ai-character-bulk-creation" element={<Navigate to="/admin/characters" replace />} />

    {/* RootAdmin Only Routes */}
    <Route element={<RootAdminGuard />}>
      <Route path="partners" element={<SuspenseWrapper><AdminPartners /></SuspenseWrapper>} />
      <Route path="partners/:id/pricing" element={<SuspenseWrapper><AdminPartnerPricing /></SuspenseWrapper>} />
      <Route path="pricing-presets" element={<SuspenseWrapper><AdminPricingPresets /></SuspenseWrapper>} />
      <Route path="platform-tariff" element={<SuspenseWrapper><AdminPlatformTariff /></SuspenseWrapper>} />
      <Route path="partner-deposit" element={<Navigate to="/admin/platform-tariff" replace />} />
      <Route path="ai-settings" element={<SuspenseWrapper><AdminAISettings /></SuspenseWrapper>} />
      <Route path="ai-costs" element={<SuspenseWrapper><AdminAICosts /></SuspenseWrapper>} />
      <Route path="ai-fallback" element={<SuspenseWrapper><AdminAIFallbackMonitoring /></SuspenseWrapper>} />
      <Route path="monitoring" element={<SuspenseWrapper><AdminSystemMonitoring /></SuspenseWrapper>} />
      <Route path="audit-logs" element={<SuspenseWrapper><AdminAuditLogs /></SuspenseWrapper>} />
      <Route path="data-transfer" element={<SuspenseWrapper><AdminDataTransfer /></SuspenseWrapper>} />
      <Route path="debug" element={<SuspenseWrapper><AdminDebug /></SuspenseWrapper>} />
    </Route>
  </>
);

export default adminRoutes;
